// src/components/PlannerActions.jsx
import React, { useRef, useState } from "react"
import { FaFileDownload, FaFileUpload, FaSpinner } from "react-icons/fa"

/**
 * Component for Export/Import functionality in the Planner
 *
 * @param {Object} props
 * @param {Object} props.productNodes - Current product nodes in the scene
 * @param {function} props.resetScene - Function to clear the scene
 * @param {React.RefObject} props.iframeRef - Reference to the Emersya iframe
 * @param {function} props.setLoadingProducts - Function to update loading state
 * @param {function} props.setProductNodes - Function to update productNodes state
 * @param {function} props.setProductNodesToAdd - Function to update productNodesToAdd state
 * @param {Object} props.nodeLocalIdsRef - Reference to track the current node ID counter
 */
const PlannerActions = ({
  productNodes,
  resetScene,
  iframeRef,
  setLoadingProducts,
  setProductNodes,
  setProductNodesToAdd,
  nodeLocalIdsRef,
}) => {
  const fileInputRef = useRef(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  /**
   * Exports the current scene to a JSON file
   */
  const exportScene = () => {
    if (Object.keys(productNodes).length === 0) {
      alert("Nessun prodotto da esportare / No products to export")
      return
    }

    setIsExporting(true)

    try {
      // Create a JSON-serializable version of productNodes
      const exportData = {
        version: "1.0",
        timestamp: new Date().toISOString(),
        products: Object.values(productNodes).map((node) => {
          // Convert Float32Array to regular array for position
          const position =
            node.position instanceof Float32Array
              ? Array.from(node.position)
              : node.position

          // Extract applied material variations
          const configurableMaterials = []
          if (
            node.configurableMaterials &&
            Array.isArray(node.configurableMaterials)
          ) {
            node.configurableMaterials.forEach((mat) => {
              if (mat.name) {
                configurableMaterials.push({
                  name: mat.name,
                  appliedVariation: mat.appliedVariation || null,
                })
              }
            })
          }

          // Save important reference data for restoring the preview
          const previewPath = node.refData?.previewPath || null
          const name =
            node.refData?.name || node.matchedLocalProduct?.name || node.SKU

          return {
            SKU: node.SKU,
            projectCode: node.projectCode,
            name: name,
            previewPath: previewPath,
            position,
            rotationAngle: node.rotationAngle || 0,
            height: node.height || 1,
            configurableMaterials,
          }
        }),
      }

      // Convert to JSON and create blob
      const jsonString = JSON.stringify(exportData, null, 2)
      const blob = new Blob([jsonString], { type: "application/json" })

      // Create download link and trigger download
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `scene-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      console.log(
        "[EXPORT] Scene exported successfully with",
        exportData.products.length,
        "products"
      )
    } catch (error) {
      console.error("[EXPORT ERROR]", error)
      alert(
        `Errore durante l'esportazione: ${error.message} / Export error: ${error.message}`
      )
    } finally {
      setIsExporting(false)
    }
  }

  /**
   * Triggers the file upload dialog
   */
  const triggerFileUpload = () => {
    fileInputRef.current.click()
  }

  /**
   * Apply a single material to a product with proper error handling
   * @param {Object} materialInfo - Material info object
   * @returns {Promise} - Promise that resolves when material is applied
   */
  const applyMaterialWithRetry = (materialInfo) => {
    return new Promise((resolve) => {
      console.log("[IMPORT] Applying material:", materialInfo)

      // First update the state to ensure our UI reflects the change
      setProductNodes((prev) => {
        const node = prev[materialInfo.localId]
        if (!node) {
          console.log(
            `[IMPORT WARNING] Node ${materialInfo.localId} not found in state`
          )
          return prev
        }

        // If the node has configurableMaterials, update the applied variation
        if (node.configurableMaterials) {
          const updatedMaterials = node.configurableMaterials.map(
            (material) => {
              if (material.name === materialInfo.configurableMaterial) {
                return {
                  ...material,
                  appliedVariation: materialInfo.materialVariation,
                  activeVariation: materialInfo.materialVariation,
                }
              }
              return material
            }
          )

          return {
            ...prev,
            [materialInfo.localId]: {
              ...node,
              configurableMaterials: updatedMaterials,
            },
          }
        }

        return prev
      })

      // Then send the message to Emersya to apply the material
      try {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "setMaterials",
            materials: [
              {
                configurableMaterial: materialInfo.configurableMaterial,
                materialVariation: materialInfo.materialVariation,
                nodeLocalIds: [materialInfo.localId],
              },
            ],
          },
          "*"
        )

        // We resolve after a small delay regardless of error - since errors will be handled by the message listener
        setTimeout(() => {
          resolve()
        }, 50)
      } catch (error) {
        console.error("[IMPORT] Error applying material:", error)
        resolve() // Continue with the next material even if this one fails
      }
    })
  }

  /**
   * Apply materials sequentially with proper delays
   * @param {Array} materialsToApply - Array of material info objects
   */
  const applyMaterialsSequentially = async (materialsToApply) => {
    console.log(
      `[IMPORT] Starting to apply ${materialsToApply.length} materials sequentially`
    )

    // Apply materials one by one with delays between each
    for (let i = 0; i < materialsToApply.length; i++) {
      // Wait for the material to be applied (with retry if needed)
      await applyMaterialWithRetry(materialsToApply[i])

      // Add a substantial delay between materials to ensure each has time to load
      if (i < materialsToApply.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 800))
      }
    }

    console.log("[IMPORT] All materials applied")
    setIsImporting(false)
  }

  /**
   * Handles the file import process
   * @param {Event} event - The file input change event
   */
  const handleFileImport = (event) => {
    const file = event.target.files[0]
    if (!file) return

    setIsImporting(true)

    // Read the file directly here and pass to Planner component
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        // Basic validation
        if (!jsonData.products || !Array.isArray(jsonData.products)) {
          throw new Error("Invalid file format: missing products array")
        }

        console.log(
          "[IMPORT] Importing scene with",
          jsonData.products.length,
          "products"
        )

        // Clear current scene before importing
        resetScene()

        // Store the starting localId for reference
        const startingLocalId = nodeLocalIdsRef.current + 1

        // Add each product to the scene
        jsonData.products.forEach((product, index) => {
          // Generate a new local ID for this product
          const localId = startingLocalId + index

          // Set loading state
          setLoadingProducts((prev) => ({
            ...prev,
            [localId]: true,
          }))

          // Prepare the node for adding to the scene
          const node = {
            SKU: product.SKU,
            projectCode: product.projectCode,
            localId,
            parentLocalId: 1,
            draggable: true,
            tracked: true,
            position: product.position,
            rotationAxis: [0, 1, 0],
            rotationAngle: product.rotationAngle || 0,
            configurableMaterials: [],
            height: product.height || 1,
          }

          // Add to productNodesToAdd queue with proper refData
          setProductNodesToAdd((prev) => ({
            ...prev,
            [localId]: {
              ...node,
              height: product.height || 1,
              refData: {
                name: product.name || product.SKU,
                projectCode: product.projectCode,
                // Use the CDN URL pattern to recreate the preview path if it's not saved
                previewPath:
                  product.previewPath ||
                  `https://cdn.emersya.com/cl/3IB82Q24UW/p/${
                    product.projectCode
                  }/productNodes/86003?date=${Date.now()}`,
              },
              // Set applied material variations as default variations
              configurableMaterials: Array.isArray(
                product.configurableMaterials
              )
                ? product.configurableMaterials.map((mat) => ({
                    ...mat,
                    activeVariation: mat.appliedVariation, // Make sure activeVariation is set
                  }))
                : [],
            },
          }))

          // Send message to add product to Emersya
          iframeRef.current.contentWindow.postMessage(
            {
              action: "addProductNodesInstances",
              id: localId,
              nodes: [node],
            },
            "*"
          )
        })

        // Update the nodeLocalIdsRef to prevent ID collisions
        nodeLocalIdsRef.current = startingLocalId + jsonData.products.length - 1

        // Create a queue to apply materials after products are loaded
        const materialsToApply = []

        // Collect all materials that need to be applied
        jsonData.products.forEach((product, index) => {
          const localId = startingLocalId + index

          if (
            product.configurableMaterials &&
            Array.isArray(product.configurableMaterials)
          ) {
            product.configurableMaterials.forEach((material) => {
              if (material.name && material.appliedVariation) {
                materialsToApply.push({
                  localId,
                  configurableMaterial: material.name,
                  materialVariation: material.appliedVariation,
                })
              }
            })
          }
        })

        // Apply materials with a longer delay to ensure products are fully loaded
        if (materialsToApply.length > 0) {
          console.log(
            "[IMPORT] Will apply",
            materialsToApply.length,
            "materials after loading"
          )

          // Increased delay from 3000ms to 10000ms to ensure everything is properly loaded
          setTimeout(() => {
            applyMaterialsSequentially(materialsToApply)
          }, 10000) // 10 second delay to ensure products are loaded and materials are available
        } else {
          // If no materials to apply, finish import process
          setTimeout(() => {
            setIsImporting(false)
          }, 2000)
        }

        // Reset file input
        event.target.value = null
      } catch (error) {
        console.error("[IMPORT ERROR]", error)
        alert(
          `Errore durante l'importazione: ${error.message} / Import error: ${error.message}`
        )
        event.target.value = null
        setIsImporting(false)
      }
    }

    reader.onerror = () => {
      console.error("[IMPORT ERROR] Failed to read file")
      alert("Errore nella lettura del file / Error reading file")
      event.target.value = null
      setIsImporting(false)
    }

    reader.readAsText(file)
  }

  return (
    <div className="ay-flex ay-gap-4 ay-mb-6 ay-mt-2">
      <button
        onClick={exportScene}
        disabled={isExporting || Object.keys(productNodes).length === 0}
        className="ay-flex ay-items-center ay-gap-2 ay-bg-black ay-text-white ay-px-4 ay-py-2 ay-hover:ay-bg-black/80 ay-transition-colors disabled:ay-opacity-50 disabled:ay-cursor-not-allowed"
        title="Esporta scena / Export scene"
      >
        {isExporting ? (
          <FaSpinner className="ay-animate-spin" />
        ) : (
          <FaFileDownload />
        )}
        <span>Esporta / Export</span>
      </button>

      <button
        onClick={triggerFileUpload}
        disabled={isImporting}
        className="ay-flex ay-items-center ay-gap-2 ay-bg-black ay-text-white ay-px-4 ay-py-2 ay-hover:ay-bg-black/80 ay-transition-colors disabled:ay-opacity-50 disabled:ay-cursor-not-allowed"
        title="Importa scena / Import scene"
      >
        {isImporting ? (
          <FaSpinner className="ay-animate-spin" />
        ) : (
          <FaFileUpload />
        )}
        <span>Importa / Import</span>
      </button>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileImport}
        accept=".json"
        className="ay-hidden"
      />
    </div>
  )
}

export default PlannerActions
